// 멀티탭 레이아웃 - 서버에서 받은 메뉴 목록 기반으로 탭 구성
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import PlaceholderPage from './PlaceholderPage';
import HomePage from '../pages/HomePage';
import { subscribeTabNav } from '../utils/tabNav';
import './TabLayout.css';

// ── 기준정보 화면 임포트 ──────────────────────────────
import AccountPage    from '../pages/reference/AccountPage';
import StockMasterPage from '../pages/reference/StockMasterPage';
import CodeMasterPage  from '../pages/reference/CodeMasterPage';

// ── 거래 화면 임포트 ─────────────────────────────────
import BuyPage            from '../pages/trade/BuyPage';
import SellPage           from '../pages/trade/SellPage';
import DividendPage       from '../pages/trade/DividendPage';
import InvestPlanPage     from '../pages/trade/InvestPlanPage';
import BalanceHistoryPage from '../pages/trade/BalanceHistoryPage';

// ── 현황 화면 임포트 ─────────────────────────────────
import ProfitStatusPage  from '../pages/status/ProfitStatusPage';
import TurnStatusPage    from '../pages/status/TurnStatusPage';
import MarketIndexPage   from '../pages/status/MarketIndexPage';
import PortfolioPage     from '../pages/status/PortfolioPage';

// ── 관리자 화면 임포트 ───────────────────────────────
import PriceCollectPage    from '../pages/status/PriceCollectPage';
import DividendSummaryPage from '../pages/status/DividendSummaryPage';
import GraphPage           from '../pages/status/GraphPage';
import SampleDataPage      from '../pages/admin/SampleDataPage';
import SystemManagePage    from '../pages/admin/SystemManagePage';
import UserManagePage      from '../pages/admin/UserManagePage';
import MenuMasterPage      from '../pages/admin/MenuMasterPage';
import AlertPage           from '../pages/admin/AlertPage';
import AlertTypePage       from '../pages/admin/AlertTypePage';

// ── 사이트탐색알림 화면 임포트 ───────────────────────
import WatchConditionPage           from '../pages/marketplace/WatchConditionPage';
import MarketplaceAlertHistoryPage  from '../pages/marketplace/MarketplaceAlertHistoryPage';

// menu_code → 컴포넌트 매핑
const COMPONENT_MAP = {
  account:      <AccountPage />,
  stock:        <StockMasterPage />,
  code:         <CodeMasterPage />,
  investplan:   <InvestPlanPage />,
  buy:          <BuyPage />,
  sell:         <SellPage />,
  dividend:     <DividendPage />,
  balance:      <BalanceHistoryPage />,
  profit:       <ProfitStatusPage />,
  roundstatus:  <TurnStatusPage />,
  divstatus:    <DividendSummaryPage />,
  graph:        <GraphPage />,
  marketindex:  <MarketIndexPage />,
  pricecollect: <PriceCollectPage />,
  sampledata:   <SampleDataPage />,
  usermgmt:     <UserManagePage />,
  menumgmt:     <MenuMasterPage />,
  AlertPage:     <AlertPage />,
  AlertTypePage:    <AlertTypePage />,
  PortfolioPage:    <PortfolioPage />,
  SystemManagePage: <SystemManagePage />,
  marketplacewatch:   <WatchConditionPage />,
  marketplacehistory: <MarketplaceAlertHistoryPage />,
};

// 상단 메뉴 바 왼쪽에 개별 버튼으로 먼저 노출할 고정 메뉴 - menu_code 기준, 이 순서대로 표시
// (해당 메뉴는 소속 그룹의 드롭다운에도 그대로 유지됨)
const PINNED_MENU_CODES = ['PortfolioPage', 'profit', 'marketindex', 'pricecollect'];

const TabLayout = () => {
  const { user, menus, logout, isAdmin } = useAuth();

  // menus를 그룹별로 정리
  const visibleGroups = useMemo(() => {
    const groupMap = {};
    menus.forEach(m => {
      if (!groupMap[m.group_code]) {
        groupMap[m.group_code] = { groupId: m.group_code, groupLabel: m.group_name, tabs: [] };
      }
      const component = COMPONENT_MAP[m.menu_code] || <PlaceholderPage title={m.menu_name} />;
      groupMap[m.group_code].tabs.push({ id: m.menu_code, label: m.menu_name, component });
    });
    // group_code 정렬: reference → trade → status → admin
    const ORDER = ['reference', 'trade', 'status', 'admin'];
    return Object.values(groupMap).sort((a, b) => {
      const ai = ORDER.indexOf(a.groupId);
      const bi = ORDER.indexOf(b.groupId);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [menus]);

  const allTabs = visibleGroups.flatMap(g => g.tabs);

  // 왼쪽 고정 메뉴 - 권한상 접근 가능한 메뉴(allTabs)에 있는 것만 노출
  const pinnedTabs = useMemo(() => {
    const tabMap = {};
    allTabs.forEach(t => { tabMap[t.id] = t; });
    return PINNED_MENU_CODES.map(code => tabMap[code]).filter(Boolean);
  }, [allTabs]);

  // 로그인 직후 및 프로젝트명 클릭 시 홈 화면(빈 상태) 표시
  const [activeTabId, setActiveTabId] = useState('');

  // 그룹 드롭다운 - 열려있는 그룹 id (클릭 또는 hover로 열림)
  // .tab-nav가 가로 스크롤(overflow-x: auto)이라 드롭다운을 그 안에 절대배치하면
  // overflow-y까지 auto로 계산되어(스펙상 x/y가 함께 묶임) 세로로 잘리는 문제가 있어,
  // 드롭다운 패널은 Portal로 body 바로 아래에 그리고 fixed 좌표로 위치만 맞춘다.
  const [openGroupId, setOpenGroupId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState(null); // { left, top }
  const groupNavRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const toggleRefs = useRef({}); // groupId → 토글 버튼 DOM
  const closeTimerRef = useRef(null);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpenGroupId(null), 150);
  };

  const openGroup = (groupId) => {
    cancelClose();
    const btn = toggleRefs.current[groupId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setDropdownPos({ left: rect.left, top: rect.bottom + 4 });
    }
    setOpenGroupId(groupId);
  };

  const toggleGroup = (groupId) => {
    if (openGroupId === groupId) {
      setOpenGroupId(null);
    } else {
      openGroup(groupId);
    }
  };

  // 드롭다운 바깥 클릭 시 닫기 (토글 버튼 영역 + Portal로 렌더된 드롭다운 패널 모두 제외)
  useEffect(() => {
    const handleClickOutside = (e) => {
      const insideNav = groupNavRef.current && groupNavRef.current.contains(e.target);
      const insideDropdown = dropdownContentRef.current && dropdownContentRef.current.contains(e.target);
      if (!insideNav && !insideDropdown) {
        setOpenGroupId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 가로 스크롤/창 크기 변경 시 좌표가 어긋나므로 열려 있으면 닫는다
  useEffect(() => {
    const navEl = groupNavRef.current;
    const closeOpen = () => setOpenGroupId(null);
    navEl && navEl.addEventListener('scroll', closeOpen);
    window.addEventListener('resize', closeOpen);
    return () => {
      navEl && navEl.removeEventListener('scroll', closeOpen);
      window.removeEventListener('resize', closeOpen);
    };
  }, []);

  useEffect(() => () => cancelClose(), []);

  const selectTab = (tabId) => {
    setActiveTabId(tabId);
    setOpenGroupId(null);
  };

  // 다른 화면(예: 건별수익률현황 메모 클릭)에서 탭 전환 요청 시 구독
  useEffect(() => {
    const unsubscribe = subscribeTabNav(({ tab }) => {
      if (tab) selectTab(tab);
    });
    return unsubscribe;
  }, []);

  // 메뉴가 없을 때 안내
  if (menus.length === 0) {
    return (
      <div className="app-layout">
        <header className="app-header">
          <div className="header-title">K<b style={{fontSize:'1.2em',margin:'0 1px'}}>·</b>G<b style={{fontSize:'1.2em',margin:'0 1px'}}>·</b>B 투자 관리 시스템</div>
          <div className="header-user">
            <span className="user-name">{user?.username}</span>
            <button className="logout-button" onClick={logout}>로그아웃</button>
          </div>
        </header>
        <main style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#888', fontSize: 14 }}>
          접근 가능한 메뉴가 없습니다. 관리자에게 권한을 요청하세요.
        </main>
      </div>
    );
  }

  return (
    <div className="app-layout">
      {/* ── 상단 헤더 ── */}
      <header className="app-header">
        <div
          className="header-title header-title-btn"
          onClick={() => selectTab('')}
          title="홈으로"
        >
          📈 K<span className="header-dot">●</span>G<span className="header-dot">●</span>B 투자 관리 시스템
        </div>
        <div className="header-user">
          <span className="user-name">
            {user?.username}
            {isAdmin && <span className="admin-badge">관리자</span>}
          </span>
          <button className="logout-button" onClick={logout}>로그아웃</button>
        </div>
      </header>

      {/* ── 탭 메뉴: 왼쪽부터 상시 노출 버튼 → 그룹 드롭다운, 한 줄 ── */}
      <nav className="tab-nav" ref={groupNavRef}>
        {pinnedTabs.map((tab) => (
          <button
            key={tab.id}
            className={`primary-tab-button ${activeTabId === tab.id ? 'active' : ''}`}
            onClick={() => selectTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}

        <span className="nav-divider" />

        {visibleGroups.map((group) => (
          <div
            key={group.groupId}
            className={`dropdown-group ${openGroupId === group.groupId ? 'open' : ''}`}
            onMouseEnter={() => openGroup(group.groupId)}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              ref={(el) => { toggleRefs.current[group.groupId] = el; }}
              className="dropdown-toggle"
              onClick={() => toggleGroup(group.groupId)}
            >
              {group.groupLabel}
              <span className="dropdown-caret">▾</span>
            </button>

            {openGroupId === group.groupId && dropdownPos && createPortal(
              <div
                ref={dropdownContentRef}
                className="dropdown-content"
                style={{ top: dropdownPos.top, left: dropdownPos.left }}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
              >
                {group.tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`dropdown-item ${activeTabId === tab.id ? 'active' : ''}`}
                    onClick={() => selectTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        ))}
      </nav>

      {/* ── 탭 컨텐츠 ── */}
      <main className="tab-content">
        {/* 홈 화면: 탭을 선택하지 않은 초기 상태 */}
        {activeTabId === '' && <HomePage />}

        {allTabs.map((tab) => {
          const isActive = activeTabId === tab.id;
          return (
            <div
              key={tab.id}
              style={{
                display: isActive ? 'flex' : 'none',
                flex: 1,
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              {React.cloneElement(tab.component, { isActive })}
            </div>
          );
        })}
      </main>
    </div>
  );
};

export default TabLayout;
